import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/lib/api';
import AdminEventEditModal from '@/components/AdminEventEditModal';
import { formatEventDate } from '@/lib/formatEventDate';
import { ArrowLeft, Search, Trash2, DollarSign, Calendar, Pencil } from 'lucide-react';

export default function AdminEventsPage() {
  const [events, setEvents] = useState([]);
  const [revenue, setRevenue] = useState({ total_revenue: 0, total_events_paid: 0 });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/admin/events').then(({ data }) => setEvents(data.events || [])),
      api.get('/admin/events/revenue').then(({ data }) => setRevenue(data)),
    ]).finally(() => setLoading(false));
  }, []);

  const handleDelete = async (eventId) => {
    if (!window.confirm('Remove this event?')) return;
    try {
      await api.delete(`/admin/events/${eventId}`);
      setEvents(prev => prev.filter(e => e.event_id !== eventId));
      toast.success('Event removed');
    } catch { toast.error('Failed to remove'); }
  };

  const filtered = events.filter(e => !search || e.title?.toLowerCase().includes(search.toLowerCase()) || e.host_name?.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#b456df] border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="admin-events-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center gap-3 mb-8">
        <Link to="/admin"><Button variant="ghost" size="sm" className="rounded-full"><ArrowLeft className="w-4 h-4" /></Button></Link>
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Event Management</h1>
          <p className="text-slate-500 mt-1">{events.length} total events</p>
        </div>
      </div>

      {/* Revenue Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <Card className="border-slate-100">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#56dfb4]/10 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-[#56dfb4]" />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>${revenue.total_revenue.toFixed(2)}</p>
              <p className="text-sm text-slate-500">Event Posting Revenue</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#df5681]/10 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-[#df5681]" />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{revenue.total_events_paid}</p>
              <p className="text-sm text-slate-500">Paid Event Listings</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search events..." className="pl-10 rounded-xl" />
      </div>

      <Card className="border-slate-100">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Host</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Fee</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(e => (
                <TableRow
                  key={e.event_id}
                  onClick={() => setEditing(e)}
                  className="cursor-pointer hover:bg-slate-50 transition-colors"
                  data-testid={`admin-event-row-${e.event_id}`}
                >
                  <TableCell className="font-medium text-sm max-w-xs truncate">
                    <span className="text-[#56c6df] hover:underline">{e.title}</span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{e.host_name}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-full text-xs capitalize">{e.event_type}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-500">{e.event_date ? formatEventDate(e.event_date) : 'N/A'}</TableCell>
                  <TableCell>
                    <Badge variant={e.status === 'canceled' ? 'destructive' : 'secondary'} className="rounded-full text-xs capitalize">
                      {e.status === 'canceled' ? 'Canceled' : e.payment_status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">${e.posting_fee?.toFixed(2)}</TableCell>
                  <TableCell className="text-sm">
                    {e.is_free === false
                      ? <span className="text-slate-700">${Number(e.cost_amount || 0).toFixed(2)}</span>
                      : <span className="text-emerald-600">Free</span>}
                  </TableCell>
                  <TableCell onClick={(ev) => ev.stopPropagation()}>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditing(e)}
                        className="text-slate-600"
                        data-testid={`admin-event-edit-btn-${e.event_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Link to={`/events/${e.event_id}`}><Button variant="ghost" size="sm" className="text-slate-500">View</Button></Link>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(e.event_id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editing && (
        <AdminEventEditModal
          event={editing}
          onClose={() => setEditing(null)}
          onSaved={updated => setEvents(prev => prev.map(ev => ev.event_id === updated.event_id ? { ...ev, ...updated } : ev))}
        />
      )}
    </div>
  );
}
